/** Фильтр каталога по языку книги.
 *
 * Язык — признак, не зависящий от автора, серии и жанра, поэтому живёт в
 * строке поиска рядом с «Читаемыми», а не в дереве: иначе нельзя было бы
 * посмотреть английские книги выбранного автора, не потеряв самого автора.
 */

import { useEffect, useRef, useState } from 'react'
import { api } from '../api'
import type { LangCount } from '../api'

const STORAGE_KEY = 'chita.langs'

/** Выбор языков переживает перезагрузку: он меняется куда реже поиска. */
export function loadLangs(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : []
  } catch {
    return []
  }
}

function saveLangs(codes: string[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(codes))
  } catch {
    /* приватный режим браузера — фильтр всё равно работает, просто не запомнится */
  }
}

interface Props {
  selected: string[]
  onChange: (codes: string[]) => void
}

export function LangFilter({ selected, onChange }: Props) {
  const [langs, setLangs] = useState<LangCount[] | null>(null)
  const [open, setOpen] = useState(false)
  const boxRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    api
      .langTree()
      .then((response) => setLangs(response.items))
      .catch(() => setLangs([]))
  }, [])

  // Закрытие по щелчку мимо: список длинный, кнопка «закрыть» была бы лишней.
  useEffect(() => {
    if (!open) return
    const onDown = (event: MouseEvent) => {
      if (!boxRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  const toggle = (code: string) => {
    const next = selected.includes(code)
      ? selected.filter((item) => item !== code)
      : [...selected, code]
    saveLangs(next)
    onChange(next)
  }

  const clear = () => {
    saveLangs([])
    onChange([])
  }

  const chosen = langs?.filter((item) => selected.includes(item.code)) ?? []
  const label =
    selected.length === 0
      ? 'Язык'
      : chosen.length === 1
        ? chosen[0].name
        : `Языков: ${selected.length}`

  return (
    <div className="langs" ref={boxRef}>
      <button
        className={`btn${selected.length ? ' btn--primary' : ''}`}
        onClick={() => setOpen(!open)}
        title="Показывать только книги на выбранных языках"
      >
        {label} <span className="langs__caret">▾</span>
      </button>

      {open && (
        <div className="langs__menu">
          {langs === null && <div className="langs__empty">Загрузка…</div>}
          {langs !== null && langs.length === 0 && (
            <div className="langs__empty">Языки не определены</div>
          )}
          {langs?.map((item) => (
            <button
              key={item.code}
              className={`langs__item${selected.includes(item.code) ? ' langs__item--on' : ''}`}
              onClick={() => toggle(item.code)}
            >
              <span className="langs__mark">{selected.includes(item.code) ? '✓' : ''}</span>
              <span className="langs__name">{item.name}</span>
              <span className="langs__count">{item.books}</span>
            </button>
          ))}
          {selected.length > 0 && (
            <button className="langs__clear" onClick={clear}>
              Сбросить
            </button>
          )}
        </div>
      )}
    </div>
  )
}
